import React, { useEffect, useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Modal, Button, Form, Table } from 'react-bootstrap';

const AllJobs = () => {
    const [jobs, setJobs] = useState([]);
    const [title, setTitle] = useState("");
    const [desc, setDesc] = useState("");
    const [jobLocation, setJobLocation] = useState([]);
    const [endDate, setEndDate] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [selectedJob, setSelectedJob] = useState(null);

    // Fetch jobs from the API
    useEffect(() => {
        const fetchJobs = async () => {
            try {
                const response = await axios.get("https://api.biznetusa.com/api/get-jobs");
                if (response.data && response.data.status === 200) {
                    setJobs(response.data.jobs);
                } else {
                    toast.error("Failed to fetch jobs.");
                }
            } catch (error) {
                console.error("Error fetching jobs:", error);
                toast.error("An error occurred while fetching jobs.");
            }
        };

        fetchJobs();
    }, []);

    // Handle form submission to post a new job
    const handleFormSubmit = async (e) => {
        e.preventDefault();

        try {
            const response = await axios.post("https://api.biznetusa.com/api/store-job", {
                title: title,
                desc: desc,
                job_location: jobLocation,
                end_date: endDate,
            });

            if (response.data && response.data.status === 200) {
                toast.success("Job posted successfully!");
                // Reset input fields after successful submission
                setTitle("");
                setDesc("");
                setJobLocation([]);
                setEndDate("");
                setShowModal(false); // Close the modal
                // Refresh job list
                const updatedJobs = await axios.get("https://api.biznetusa.com/api/get-jobs");
                if (updatedJobs.data && updatedJobs.data.status === 200) {
                    setJobs(updatedJobs.data.jobs);
                }
            } else {
                toast.error("Failed to post the job.");
            }
        } catch (error) {
            console.error("Error posting job:", error);
            toast.error("An error occurred while posting the job.");
        }
    };

    // Handle form submission to update a job
    const handleUpdateFormSubmit = async (e) => {
        e.preventDefault();

        if (!selectedJob) return;

        try {
            const response = await axios.put(`https://api.biznetusa.com/api/update-job/${selectedJob.id}`, {
                title: title,
                desc: desc,
                job_location: jobLocation,
                end_date: endDate,
            });

            if (response.data && response.data.status === 200) {
                toast.success("Job updated successfully!");
                setTitle("");
                setDesc("");
                setJobLocation([]);
                setEndDate("");
                setShowUpdateModal(false); // Close the modal
                setSelectedJob(null);
                // Refresh job list
                const updatedJobs = await axios.get("https://api.biznetusa.com/api/get-jobs");
                if (updatedJobs.data && updatedJobs.data.status === 200) {
                    setJobs(updatedJobs.data.jobs);
                }
            } else {
                toast.error("Failed to update the job.");
            }
        } catch (error) {
            console.error("Error updating job:", error);
            toast.error("An error occurred while updating the job.");
        }
    };

    // Handle deleting a job
    const handleDeleteJob = async (id) => {
        try {
            const response = await axios.delete(`https://api.biznetusa.com/api/deletejob/${id}`);
            if (response.data && response.data.status === 200) {
                toast.success("Job deleted successfully!");
                // Refresh job list
                const updatedJobs = await axios.get("https://api.biznetusa.com/api/get-jobs");
                if (updatedJobs.data && updatedJobs.data.status === 200) {
                    setJobs(updatedJobs.data.jobs);
                }
            } else {
                toast.error("Failed to delete the job.");
            }
        } catch (error) {
            console.error("Error deleting job:", error);
            toast.error("An error occurred while deleting the job.");
        }
    };

    const handleJobLocationChange = (e, index) => {
        const locations = [...jobLocation];
        locations[index] = e.target.value;
        setJobLocation(locations);
    };

    const addJobLocationField = () => {
        setJobLocation([...jobLocation, ""]);
    };

    const openUpdateModal = (job) => {
        setSelectedJob(job);
        setTitle(job.title);
        setDesc(job.description);
        setJobLocation(job.job_location.map((loc) => loc.location_id));
        setEndDate(job.end_date);
        setShowUpdateModal(true);
    };

    return (
        <>
            <div className="container mt-4">
                <h2>Job List</h2>
                <Button variant="primary" className="mt-4" onClick={() => setShowModal(true)}>
                    Post a New Job
                </Button>
                <Table bordered className="mt-3">
                    <thead>
                        <tr>
                            <th>Title</th>
                            <th>Description</th>
                            <th>End Date</th>
                            <th>Locations</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {jobs.length > 0 ? (
                            jobs.map((job) => (
                                <tr key={job.id}>
                                    <td>{job.title}</td>
                                    <td>{job.description}</td>
                                    <td>{job.end_date}</td>
                                    <td>
                                        {job.job_location.length > 0
                                            ? job.job_location.map((loc) => loc.location_id).join(", ")
                                            : "Not specified"}
                                    </td>
                                    <td>
                                        <Button variant="warning" onClick={() => openUpdateModal(job)} className="me-2">
                                            Update
                                        </Button>
                                        <Button variant="danger" onClick={() => handleDeleteJob(job.id)}>
                                            Delete
                                        </Button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="5" className="text-center">No jobs available</td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            {/* Modal for Posting a New Job */}
            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Post a Job</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleFormSubmit}>
                        <Form.Group className="mb-3" controlId="title">
                            <Form.Label>Job Title</Form.Label>
                            <Form.Control
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="desc">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                value={desc}
                                onChange={(e) => setDesc(e.target.value)}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="jobLocation">
                            <Form.Label>Job Locations</Form.Label>
                            {jobLocation.map((loc, index) => (
                                <Form.Control
                                    key={index}
                                    type="text"
                                    className="mb-2"
                                    value={loc}
                                    onChange={(e) => handleJobLocationChange(e, index)}
                                    placeholder={`Location ${index + 1}`}
                                    required
                                />
                            ))}
                            <Button variant="secondary" className="mt-2" onClick={addJobLocationField}>
                                Add Location
                            </Button>
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="endDate">
                            <Form.Label>End Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                required
                            />
                        </Form.Group>
                        <Button type="submit" variant="primary" className="w-100">Submit</Button>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Modal for Updating a Job */}
            <Modal show={showUpdateModal} onHide={() => setShowUpdateModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Update Job</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleUpdateFormSubmit}>
                        <Form.Group className="mb-3" controlId="title">
                            <Form.Label>Job Title</Form.Label>
                            <Form.Control
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="desc">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                value={desc}
                                onChange={(e) => setDesc(e.target.value)}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="jobLocation">
                            <Form.Label>Job Locations</Form.Label>
                            {jobLocation.map((loc, index) => (
                                <Form.Control
                                    key={index}
                                    type="text"
                                    className="mb-2"
                                    value={loc}
                                    onChange={(e) => handleJobLocationChange(e, index)}
                                    placeholder={`Location ${index + 1}`}
                                    required
                                />
                            ))}
                            <Button variant="secondary" className="mt-2" onClick={addJobLocationField}>
                                Add Location
                            </Button>
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="endDate">
                            <Form.Label>End Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                required
                            />
                        </Form.Group>
                        <Button type="submit" variant="primary" className="w-100">Update</Button>
                    </Form>
                </Modal.Body>
            </Modal>

            <ToastContainer />
        </>
    );
};

export default AllJobs;
